import {
  AbandonedOutdoorActivityResultType,
  Activity,
  AutoClearCooldownFromFeedOutdoorActivityType,
  BouncedOutdoorActivityResultType,
  Camera,
  Complete2WAOutdoorActivityResultType,
  DroppedOutdoorActivityResultType,
  EngagementPromotedToInvestigateOutdoorActivityResultType,
  EngagementQuickClearedOutdoorActivityResultType,
  EngagementResolvedOutdoorActivityResultType,
  EscalateToIntrusionWithDescriptionOutdoorActivityType,
  FinalDocumentationOutdoorActivityType,
  MonitoringSpecialistTriageOutdoorActivityType,
  No2WAOutdoorActivityResultType,
  NumberOfPeopleObserved,
  OutdoorEngagement,
  OutdoorEngagementResolutionCategory,
  PeopleObservedRemainedInCameraView,
  QuickClearFromInvestigateOutdoorActivityType,
  TwoWayAudioInteractionOutdoorActivityType,
  UnfamiliarFaceTwoWayAudioParticipantType
} from '../../../../api/models'
import { getOutdoorEngagementResolutionCategoryDisplayName } from '../../../../constants/OutdoorEngagementResolutionCategoryDisplayNames'
import { findCameraName, formatTimelineTime } from '../ertUtils'
import {
  countMatchedFamiliarFaces,
  findActivityOfType,
  findFamiliarFaceReview,
  getAutoClearedActivity,
  getEndingActivity,
  getFailedEndingActivity,
  getFailedEndingKind,
  getLastActivity,
  neverReachedSpecialist,
  RRWebSummary,
  TimelineFragmentKind,
  wasAutoCleared,
  wasClearedByCustomer
} from './customerCareTimelineUtils'

export const PEOPLE_OBSERVED_COUNT_LABEL: Record<NumberOfPeopleObserved, string> = {
  [NumberOfPeopleObserved.None]: '0',
  [NumberOfPeopleObserved.One]: '1',
  [NumberOfPeopleObserved.Two]: '2',
  [NumberOfPeopleObserved.Several]: 'Several',
  [NumberOfPeopleObserved.Unclear]: 'Unclear'
}

export const KNOWN_PERSON_LABEL = 'A known person'
export const UNKNOWN_PERSON_LABEL = 'An unknown person'

export function getWhatWasSeenValue(engagement: OutdoorEngagement, cameras: Camera[]): string | undefined {
  const cameraName = findCameraName(engagement.triggeringDevice.id, cameras)
  const cameraNameToDisplay = cameraName || 'Unknown'
  const personLabel = engagement.result?.matchedFamiliarFaces?.length ? KNOWN_PERSON_LABEL : UNKNOWN_PERSON_LABEL
  return `${personLabel} · ${cameraNameToDisplay} camera`
}

export const HANDLED_BY_AGENT_TEXT = 'An Outdoor Monitoring Agent'
export const HANDLED_BY_NO_AGENT_TEXT = 'No agent. Cleared automatically.'
export const UNABLE_TO_VIEW_VIDEO = 'Unknown. Unable to view video.'
export const UNKNOWN_DUE_TO_ERROR_TEXT = 'Unknown due to error retrieving data.'
export const UNKNOWN_TEXT = 'Unknown'

// Anything that didn't clear itself automatically (cooldown or Camera AI) reached a human specialist.
export function getHandledByValue(engagement: OutdoorEngagement, activities: Activity[]): string {
  return neverReachedSpecialist(engagement, activities) ? HANDLED_BY_NO_AGENT_TEXT : HANDLED_BY_AGENT_TEXT
}

export const REACHED_FEED_NEVER_PICKED_UP_TEXT = 'Event was never picked up'

export function getReviewedInFeedValue(
  engagement: OutdoorEngagement,
  activities: Activity[],
  rrwebSummary: RRWebSummary | undefined
): string | undefined {
  if (neverReachedSpecialist(engagement, activities)) return REACHED_FEED_NEVER_PICKED_UP_TEXT
  if (!rrwebSummary) return undefined

  const { isLive, hasSuccessfulRecording } = rrwebSummary
  if (!isLive && !hasSuccessfulRecording) return undefined
  const watched = isLive && hasSuccessfulRecording ? 'live and recorded video' : isLive ? 'live video' : 'recorded video'

  // Bounced/dropped/abandoned are decided by the last activity alone (see getFailedEndingKind), so a stale
  // result on the triage activity can't contradict the timeline's ending or the Outcome.
  switch (getFailedEndingKind(engagement, activities)) {
    case TimelineFragmentKind.Bounced:
      return `Watched ${watched}; event bounced`
    case TimelineFragmentKind.Dropped:
      return `Watched ${watched}; event dropped`
    case TimelineFragmentKind.Abandoned:
      return `Watched ${watched}; event was abandoned`
  }

  const triageResultType = findActivityOfType(activities, MonitoringSpecialistTriageOutdoorActivityType.MonitoringSpecialistTriage)?.completed?.result
    .type

  switch (triageResultType) {
    case EngagementPromotedToInvestigateOutdoorActivityResultType.EngagementPromotedToInvestigate:
    case EngagementResolvedOutdoorActivityResultType.EngagementResolved:
      return `Watched ${watched}; sent on for a closer look`
    case EngagementQuickClearedOutdoorActivityResultType.EngagementQuickCleared:
      return `Watched ${watched}; cleared`
    default:
      // The customer's clear outranks a failed ending (see getFailedEndingActivity), so it still has something to say here
      return wasClearedByCustomer(engagement, activities) ? `Watched ${watched}; cleared` : undefined
  }
}

// Same activity the timeline's failed ending row is built from, so the Outcome can't contradict it
const getOutcomeActivity = (engagement: OutdoorEngagement, activities: Activity[]): Activity | undefined =>
  getFailedEndingActivity(engagement, activities) ?? getEndingActivity(activities)

function getOutcomeForResultType(resultType: string | undefined): string | undefined {
  switch (resultType) {
    case EngagementQuickClearedOutdoorActivityResultType.EngagementQuickCleared:
      return 'Cleared after a quick look'
    case BouncedOutdoorActivityResultType.Bounced:
      return 'Event bounced'
    case DroppedOutdoorActivityResultType.Dropped:
      return 'Event dropped'
    case AbandonedOutdoorActivityResultType.Abandoned:
      return 'Event was abandoned'
    default:
      return undefined
  }
}

const AUTO_CLEARED_OUTCOME_TEXT = 'Auto-cleared'

export function getOutcomeValue(engagement: OutdoorEngagement, activities: Activity[]): string | undefined {
  if (findActivityOfType(activities, EscalateToIntrusionWithDescriptionOutdoorActivityType.EscalateToIntrusionWithDescription)) {
    return 'Escalated to Intrusion with description'
  }

  if (engagement.result?.resolution?.category === OutdoorEngagementResolutionCategory.ClearedByAI) {
    return 'Cleared by Camera AI'
  }

  // The customer disarming clears the engagement without an agent, same as an auto-clear cooldown.
  if (engagement.result?.resolution?.category === OutdoorEngagementResolutionCategory.ClearedByCustomer) {
    return AUTO_CLEARED_OUTCOME_TEXT
  }

  const autoClearedActivity = getAutoClearedActivity(activities)
  if (autoClearedActivity) {
    return autoClearedActivity.type === AutoClearCooldownFromFeedOutdoorActivityType.AutoClearCooldownFromFeed
      ? AUTO_CLEARED_OUTCOME_TEXT
      : 'Investigation → Auto-cleared'
  }

  const triageResultType = findActivityOfType(activities, MonitoringSpecialistTriageOutdoorActivityType.MonitoringSpecialistTriage)?.completed?.result
    .type

  switch (triageResultType) {
    case EngagementPromotedToInvestigateOutdoorActivityResultType.EngagementPromotedToInvestigate:
    case EngagementResolvedOutdoorActivityResultType.EngagementResolved:
      if (findActivityOfType(activities, FinalDocumentationOutdoorActivityType.FinalDocumentation)) {
        return 'Investigation → Final Documentation'
      }
      if (findActivityOfType(activities, QuickClearFromInvestigateOutdoorActivityType.QuickClearFromInvestigate)) {
        return 'Investigation → Cleared'
      }
      return getOutcomeForResultType(getOutcomeActivity(engagement, activities)?.completed?.result?.type)
    default: {
      const lastActivityOutcome = getOutcomeForResultType(getOutcomeActivity(engagement, activities)?.completed?.result?.type)
      if (lastActivityOutcome) return lastActivityOutcome
      return triageResultType === EngagementQuickClearedOutdoorActivityResultType.EngagementQuickCleared
        ? getOutcomeForResultType(triageResultType)
        : undefined
    }
  }
}

export function getCategoryValue(engagement: OutdoorEngagement): string | undefined {
  const category = engagement.result?.resolution?.category
  return category && getOutdoorEngagementResolutionCategoryDisplayName(category)
}

export function getTwoWayAudioValue(engagement: OutdoorEngagement, activities: Activity[], timezone: string | undefined): string | undefined {
  const interaction = engagement.result?.twoWayAudioInteraction
  if (!interaction) return 'Not attempted'
  if (interaction.type === No2WAOutdoorActivityResultType.NoTwoWayAudioInteraction) {
    return 'Not attempted. This outcome resolves without investigation.'
  }

  const activityTimestamp = findActivityOfType(activities, TwoWayAudioInteractionOutdoorActivityType.TwoWayAudioInteraction)?.completed?.timestamp
  const label = interaction.type === Complete2WAOutdoorActivityResultType.CompleteTwoWayAudioInteraction ? 'Complete' : 'Incomplete'
  // The result says 2WA happened, so the status still shows when the activity has no completion time to report.
  const spokeAt = activityTimestamp ? ` Spoke at ${formatTimelineTime(activityTimestamp, timezone)}.` : ''
  return `${label}.${spokeAt} Audio quality: ${interaction.audioQuality}.`
}

export const EMPTY_PERSON_IDENTIFIED_TEXT = "Unknown. We couldn't speak to them."

export function getPersonIdentifiedAsValue(engagement: OutdoorEngagement): string | undefined {
  const participants = engagement.result?.twoWayAudioInteraction?.participants ?? []
  const unfamiliarParticipant = participants.find(
    (participant): participant is { type: UnfamiliarFaceTwoWayAudioParticipantType; name: string } =>
      participant.type === UnfamiliarFaceTwoWayAudioParticipantType.UnfamiliarFace
  )
  return unfamiliarParticipant?.name
}

export const EMPTY_STATED_PURPOSE_TEXT = 'None'

export function getStatedPurposeValue(engagement: OutdoorEngagement): string | undefined {
  const statedPurpose = engagement.result?.twoWayAudioInteraction?.statedPurpose
  return statedPurpose ? `'${statedPurpose}'` : undefined
}

export const EMPTY_FLAGGED_ISSUES_TEXT = 'None flagged.'

export function getFlaggedIssuesValue(engagement: OutdoorEngagement): string | undefined {
  const flaggedIssues = engagement.result?.twoWayAudioInteraction?.flaggedIssues
  return flaggedIssues && flaggedIssues.length > 0 ? flaggedIssues.join(', ') : undefined
}

export const EMPTY_AGENT_NOTES_TEXT = 'N/A'

// A quick clear earlier in the timeline doesn't count if something happened after it (e.g. the engagement
// was re-opened) - only the last chronological activity determines whether it's still quick-cleared.
function wasQuickCleared(activities: Activity[]): boolean {
  const lastActivity = getLastActivity(activities)
  if (!lastActivity) return false
  if (lastActivity.type === QuickClearFromInvestigateOutdoorActivityType.QuickClearFromInvestigate) return true
  return (
    lastActivity.type === MonitoringSpecialistTriageOutdoorActivityType.MonitoringSpecialistTriage &&
    lastActivity.completed?.result?.type === EngagementQuickClearedOutdoorActivityResultType.EngagementQuickCleared
  )
}

export function getAgentNotesValue(engagement: OutdoorEngagement, activities: Activity[]): string | undefined {
  if (wasQuickCleared(activities) || wasAutoCleared(activities)) return undefined

  const escalateResult = findActivityOfType(activities, EscalateToIntrusionWithDescriptionOutdoorActivityType.EscalateToIntrusionWithDescription)
    ?.completed?.result
  const escalationDescription = escalateResult && 'escalationDetails' in escalateResult ? escalateResult.escalationDetails.description : undefined

  const note = engagement.result?.internalNote || escalationDescription
  return note ? `'${note}'` : undefined
}

export function getPeopleObservedValue(engagement: OutdoorEngagement, activities: Activity[]): string | undefined {
  const finalDocumentationResult = findActivityOfType(activities, FinalDocumentationOutdoorActivityType.FinalDocumentation)?.completed?.result
  const peopleObserved =
    finalDocumentationResult && 'peopleObserved' in finalDocumentationResult ? finalDocumentationResult.peopleObserved : undefined

  if (peopleObserved) {
    const viewLabel =
      peopleObserved.remainedInCameraView === PeopleObservedRemainedInCameraView.RemainedInCameraView ? 'remained in view' : 'left view'
    return `${PEOPLE_OBSERVED_COUNT_LABEL[peopleObserved.numberOfPeopleObserved]} · ${viewLabel}`
  }

  const escalationCount = engagement.result?.escalation?.numberOfPeopleObserved
  return escalationCount ? PEOPLE_OBSERVED_COUNT_LABEL[escalationCount] : undefined
}

export const EMPTY_FAMILIAR_FACE_REVIEW_TEXT = 'Not performed'

export function getFamiliarFaceReviewValue(activities: Activity[]): string | undefined {
  const activity = findFamiliarFaceReview(activities)
  if (!activity) return undefined

  const matchCount = countMatchedFamiliarFaces(activity)
  return `${matchCount} match${matchCount === 1 ? '' : 'es'}`
}
